import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DemoRequestForm = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    platform: '',
    followerCount: '',
  });

  const platforms = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'twitter', label: 'X (Twitter)' },
    { value: 'multiple', label: 'Multiple Platforms' },
  ];

  const followerRanges = [
    { value: '10k-50k', label: '10K - 50K' },
    { value: '50k-100k', label: '50K - 100K' },
    { value: '100k-500k', label: '100K - 500K' },
    { value: '500k-1m', label: '500K - 1M' },
    { value: '1m+', label: '1M+' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.platform || !formData.followerCount) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/demo-request`, formData);
      setIsSuccess(true);
      toast.success('Demo request submitted! We\'ll be in touch soon.');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Demo request error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-cyan-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Request Received!
        </h3>
        <p className="text-slate-400">
          Our team will reach out within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="demo-request-form">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-slate-300">Full Name</Label>
        <Input
          id="name"
          name="name"
          type="text"
          placeholder="Your name"
          value={formData.name}
          onChange={handleChange}
          className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white placeholder:text-slate-500"
          data-testid="demo-form-name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="text-slate-300">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="you@example.com"
          value={formData.email}
          onChange={handleChange}
          className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white placeholder:text-slate-500"
          data-testid="demo-form-email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="text-slate-300">Phone Number</Label>
        <Input
          id="phone"
          name="phone"
          type="tel"
          placeholder="+91 98765 43210"
          value={formData.phone}
          onChange={handleChange}
          className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white placeholder:text-slate-500"
          data-testid="demo-form-phone"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-slate-300">Primary Platform</Label>
        <Select onValueChange={(value) => handleSelectChange('platform', value)} value={formData.platform}>
          <SelectTrigger 
            className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white"
            data-testid="demo-form-platform"
          >
            <SelectValue placeholder="Select your platform" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {platforms.map((platform) => (
              <SelectItem 
                key={platform.value} 
                value={platform.value}
                className="text-white hover:bg-slate-800 focus:bg-slate-800"
              >
                {platform.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-300">Follower Count</Label>
        <Select onValueChange={(value) => handleSelectChange('followerCount', value)} value={formData.followerCount}>
          <SelectTrigger 
            className="bg-slate-950 border-slate-800 focus:border-cyan-400 focus:ring-cyan-400/20 rounded-xl h-12 text-white"
            data-testid="demo-form-followers"
          >
            <SelectValue placeholder="Select follower range" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            {followerRanges.map((range) => (
              <SelectItem 
                key={range.value} 
                value={range.value}
                className="text-white hover:bg-slate-800 focus:bg-slate-800"
              >
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 rounded-full h-14 font-bold text-base transition-all hover:scale-[1.02]"
        data-testid="demo-form-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          'Request Demo'
        )}
      </Button>
    </form>
  );
};
